import React, { useState, useEffect } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column, ColumnBodyOptions } from 'primereact/column';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';

import hostsService from '../../service/api/HostsService';

import divisionsService from '../../service/api/DivisionsService';

import printersService from '../../service/api/PrintersService';
import { Dialog } from 'primereact/dialog';
import PrinterDialog from './PrinterDialog';
import { Trans, useTranslation } from 'react-i18next';
import { Printer } from '../../types/Printer';
import WriteOnlyButton from '../controls/WriteOnlyButton';

export const PrinterManagement = () => {
  const [globalFilter, setGlobalFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const [printers, setPrinters] = useState<Printer[]>(null);
  const [printerToDelete, setPrinterToDelete] = useState<Printer>();

  const [hosts, setHosts] = useState<any[]>();

  const [divisions, setDivisions] = useState<any[]>();

  const [selectedRowToEdit, setSelectedRowToEdit] = useState<Printer>(null);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [addEditDialogVisible, setAddEditDialogVisible] = useState(false);
  const { t } = useTranslation();

  const loadData = () => {
    setLoading(true);
    Promise.all([printersService.getAll(), hostsService.getActive(), divisionsService.getAll()]).then((response) => {
      setHosts(response[1]);
      const hostsDict = Object.assign({}, ...response[1].map((r) => ({ [r.id]: r.name })));

      setDivisions(response[2]);
      const divisionsDict = Object.assign({}, ...response[2].map((r) => ({ [r.id]: r.name })));

      const _printers = response[0];
      _printers.forEach((_x) => {
        _x.hostName = hostsDict[_x.hostId];

        _x.divisionName = divisionsDict[_x.divisionId];
      });
      setPrinters(_printers);
      setLoading(false);
    });
  };

  useEffect(() => {
    loadData();
  }, []);

  const printersTableHeader = (
    <div className="table-header">
      {t('printers.header')}
      <span className="p-input-icon-left">
        <i className="pi pi-search" />
        <InputText
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t('common.search')}
        />
        <Button
          type="button"
          icon="pi pi-plus"
          label={t('common.add')}
          className="p-button ml-2"
          onClick={() => {
            addPrinter();
          }}
        ></Button>
      </span>
    </div>
  );

  const closeAddEditModal = () => {
    setAddEditDialogVisible(false);
    loadData();
  };

  const confirmDeletePrinter = (printer: Printer) => {
    setPrinterToDelete(printer);
    setDeleteDialogVisible(true);
  };

  const deletePrinter = () => {
    setLoading(true);
    printersService.delete(printerToDelete.id).then(() => {
      loadData();
    });

    setDeleteDialogVisible(false);
  };
  const hideDeleteDialog = () => {
    setDeleteDialogVisible(false);
  };

  const editPrinter = (_printer: Printer) => {
    setSelectedRowToEdit(_printer);
    setAddEditDialogVisible(true);
  };

  const addPrinter = () => {
    setSelectedRowToEdit(null);
    setAddEditDialogVisible(true);
  };

  const deleteDialogFooter = (
    <>
      <Button label={t('common.no')} icon="pi pi-times" className="p-button-text" onClick={() => hideDeleteDialog()} />
      <WriteOnlyButton
        label={t('common.yes')}
        icon="pi pi-check"
        className="p-button-text"
        onClick={() => deletePrinter()}
      />
    </>
  );

  const bodyTemplate = (data: Printer, props: ColumnBodyOptions) => {
    return <>{data[props.field]}</>;
  };

  const actionTemplate = (data: Printer) => (
    <span>
      <Button
        type="button"
        icon="pi pi-pencil"
        tooltip={t('common.edit')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success mr-2"
        onClick={() => {
          editPrinter(data);
        }}
      ></Button>
      <WriteOnlyButton
        type="button"
        icon="pi pi-trash"
        tooltip={t('common.delete')}
        tooltipOptions={{ position: 'top' }}
        className="p-button-success"
        onClick={() => {
          confirmDeletePrinter(data);
        }}
      ></WriteOnlyButton>
    </span>
  );

  return (
    <div className="grid crud-demo">
      <div className="col-12">
        <div className="card">
          <DataTable
            value={printers}
            paginator
            className="p-datatable-customers"
            rows={10}
            dataKey="id"
            rowHover
            globalFilter={globalFilter}
            emptyMessage={t('printers.empty_message')}
            loading={loading}
            header={printersTableHeader}
          >
            <Column field="name" header={t('common.name')} sortable body={bodyTemplate}></Column>
            <Column field="hostName" header={t('printers.host')} sortable body={bodyTemplate}></Column>
            <Column field="divisionName" header={t('printers.division')} sortable body={bodyTemplate}></Column>
            <Column field="description" header={t('printers.description')} sortable body={bodyTemplate}></Column>
            <Column field="connectionType" header={t('printers.connection_type')} sortable body={bodyTemplate}></Column>
            <Column
              headerStyle={{ width: '8rem' }}
              header={t('common.actions')}
              align="center"
              body={actionTemplate}
            ></Column>
          </DataTable>
          <PrinterDialog
            dialogVisible={addEditDialogVisible}
            closeAddEditModal={closeAddEditModal}
            printer={selectedRowToEdit}
            availableHosts={hosts}
            availableDivisions={divisions}
          />
          <Dialog
            visible={deleteDialogVisible}
            className="p-fluid"
            header={
              <span style={{ display: 'flex', alignItems: 'center' }}>
                <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                {t('common.confirm')}
              </span>
            }
            modal
            footer={deleteDialogFooter}
            onHide={hideDeleteDialog}
            breakpoints={{ '896px': '90vw' }}
            style={{ minWidth: '450px' }}
          >
            <div className="confirmation-content">
              {printerToDelete && (
                <span>
                  <Trans
                    i18nKey="common.confirm_delete_message"
                    values={{ name: printerToDelete.name }}
                    components={[<strong />]}
                  />
                </span>
              )}
            </div>
          </Dialog>
        </div>
      </div>
    </div>
  );
};
